import { MULTIPLAYER_CONFIG } from "../../config/multiplayer.ts";
import type { InputIntent } from "../../input/intents.ts";
import type {
  CharacterMotionRequest,
  CharacterMotionResolution,
} from "../../physics/contracts.ts";
import type { RoadVehicleQueries } from "../vehicles/index.ts";
import type { PublicTransportQueries } from "../publicTransport/index.ts";
import { stepMultiplayerRoom } from "./tick.ts";
import type { MultiplayerRoomSnapshot } from "./types.ts";

export interface MultiplayerPredictionOptions {
  readonly playerId: string;
  readonly pendingIntents: readonly Readonly<InputIntent>[];
  readonly roadVehicleQueries?: Readonly<RoadVehicleQueries>;
  readonly publicTransportQueries?: Readonly<PublicTransportQueries>;
  readonly resolveLocalCharacterMotion?: (
    request: Readonly<CharacterMotionRequest>,
  ) => Readonly<CharacterMotionResolution>;
}

/**
 * Replays only the unacknowledged local input horizon. The shared kernel still
 * performs the prediction so vehicle, boat and elevator transitions use the
 * same ordering as room authority; non-local character physics remains on the
 * authoritative snapshot and is rendered through remote interpolation.
 */
export function predictMultiplayerRoomSnapshot(
  authoritative: Readonly<MultiplayerRoomSnapshot>,
  options: Readonly<MultiplayerPredictionOptions>,
): MultiplayerRoomSnapshot {
  let predicted = structuredClone(authoritative);
  const intents = [...options.pendingIntents]
    .sort((left, right) => left.tick - right.tick)
    .slice(-MULTIPLAYER_CONFIG.inputBatchMaximum);
  for (const pending of intents) {
    const nextTick = predicted.game.tick + 1;
    predicted = stepMultiplayerRoom(
      predicted,
      [{ ...pending, playerId: options.playerId, tick: nextTick }],
      1 / MULTIPLAYER_CONFIG.serverTickRateHz,
      {
        ...(options.roadVehicleQueries === undefined
          ? {}
          : { roadVehicleQueries: options.roadVehicleQueries }),
        ...(options.publicTransportQueries === undefined
          ? {}
          : { publicTransportQueries: options.publicTransportQueries }),
        ...(options.resolveLocalCharacterMotion === undefined
          ? {}
          : {
              resolveCharacterMotions: (requests) => requests
                .filter((request) => request.playerId === options.playerId)
                .map(options.resolveLocalCharacterMotion!),
            }),
      },
    );
  }
  return predicted;
}
